import type { ResetAskedDto, ResetDoneDto, ResetLinkDto } from '@nsb/core';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { api, ApiError, errorText, queryClient } from '../api.ts';
import { BrandLockup } from '../components/Brand.tsx';
import { Button, ErrorLine } from '../components/ui.tsx';
import { PasswordForm } from './Account.tsx';
import { linkProblemWords, resetDoneWords, resetSentWords } from './resetWords.ts';

/** Where the reset page stands: opening its link, choosing the password, done, a link that does not work, or a new one sent. */
export type ResetPageState =
  | { at: 'opening' }
  | { at: 'choose'; email: string }
  | { at: 'done'; redirect: string; signedOut: number }
  | { at: 'problem'; code: string | null; message: string | null }
  | { at: 'again'; sent: ResetAskedDto };

/** The answers that mean the link itself does not work (used, run out, never one of ours), not the password chosen. */
const LINK_CODES = ['link_unknown', 'link_used', 'expired'];
const problemOf = (e: unknown): ResetPageState => ({ at: 'problem', code: e instanceof ApiError ? e.code : null, message: errorText(e) });

/**
 * A link to choose a new password lands here (Jason, 2026-10-08: "the link brings the user to a password reset page
 * where they enter their new password"). The token is in the fragment, as a sign-in link carries it, taken out of the
 * address bar before anything else. Opening the page spends nothing, so a mail scanner opening it cannot: only saving
 * the new password does, and that signs them in.
 */
export function ResetPassword() {
  const navigate = useNavigate();
  const [token] = useState(() => new URLSearchParams(window.location.hash.slice(1)).get('t'));
  const [state, setState] = useState<ResetPageState>(token ? { at: 'opening' } : { at: 'problem', code: 'incomplete', message: null });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const once = useRef(false);
  useEffect(() => {
    if (once.current || !token) return;
    once.current = true;
    window.history.replaceState(null, '', window.location.pathname);
    api<ResetLinkDto>('/auth/reset/link', { body: { token } })
      .then((r) => setState({ at: 'choose', email: r.email }))
      .catch((e) => setState(problemOf(e)));
  }, [token]);

  const save = async (password: string) => {
    try {
      const r = await api<ResetDoneDto>('/auth/reset/password', { body: { token, password }, notice: 'Password changed' });
      await queryClient.invalidateQueries();
      setState({ at: 'done', redirect: r.redirect, signedOut: r.signedOut });
    } catch (e) {
      // Used in another tab, or run out while the page was open: the page says so, with the way to a new one.
      if (e instanceof ApiError && LINK_CODES.includes(e.code)) setState(problemOf(e));
      else throw e;
    }
  };

  const again = async () => {
    setBusy(true);
    setError(null);
    try {
      setState({ at: 'again', sent: await api<ResetAskedDto>('/auth/reset/again', { body: { token } }) });
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <ResetPasswordView
      state={state}
      busy={busy}
      error={error}
      onSave={save}
      onAgain={() => void again()}
      onContinue={(to) => navigate(to, { replace: true })}
      onAsk={() => navigate('/login?forgot=1')}
    />
  );
}

/** The reset page as drawn for each place it stands, the sign-in page's sibling: the lockup, then one thing to do. */
export function ResetPasswordView({
  state,
  busy,
  error,
  onSave,
  onAgain,
  onContinue,
  onAsk,
}: {
  state: ResetPageState;
  busy: boolean;
  error: string | null;
  onSave: (password: string) => Promise<void>;
  onAgain: () => void;
  onContinue: (to: string) => void;
  onAsk: () => void;
}) {
  return (
    // No header here, as on the sign-in link's page: the lockup and the page stand alone, centred.
    <main className="shell pb-28 pt-8 sm:pt-12">
      <div className="mx-auto max-w-sm pt-12 sm:pt-20">
        <div className="mb-14" role="img" aria-label="Never Stop Building, build portal">
          <BrandLockup />
        </div>
        <ResetBody state={state} busy={busy} error={error} onSave={onSave} onAgain={onAgain} onContinue={onContinue} onAsk={onAsk} />
      </div>
    </main>
  );
}

function ResetBody({ state, busy, error, onSave, onAgain, onContinue, onAsk }: Parameters<typeof ResetPasswordView>[0]) {
  if (state.at === 'opening') return <p className="label animate-pulse">Opening the link</p>;
  if (state.at === 'choose') {
    return (
      <>
        <h1 className="text-[2.5rem]">Choose a new password</h1>
        <p className="mb-8 mt-2 text-ink-2">For {state.email}. Saving it signs you in here, and out on your other devices.</p>
        <PasswordForm email={state.email} asksCurrent={false} submitWords="Save and sign in" save={(password) => onSave(password)} />
      </>
    );
  }
  if (state.at === 'done') {
    return (
      <>
        <h1 className="text-[2.5rem]">Password changed</h1>
        <p className="mt-2 text-ink-2">{resetDoneWords(state.signedOut)}</p>
        <div className="mt-8">
          <Button onClick={() => onContinue(state.redirect)}>Continue</Button>
        </div>
      </>
    );
  }
  if (state.at === 'again') return <ResetSent sent={state.sent} to={null} />;
  const w = linkProblemWords(state.code, state.message);
  return (
    <>
      <p className="text-lg">{w.line}</p>
      {w.detail ? <p className="mt-2 text-ink-2">{w.detail}</p> : null}
      <ErrorLine error={error} />
      <div className="mt-6">
        {w.again ? (
          <Button busy={busy} onClick={onAgain}>
            Send me a new one
          </Button>
        ) : (
          <Button onClick={onAsk}>Ask for a new link</Button>
        )}
      </div>
    </>
  );
}

/** A link to choose a new password asked for: where it went and how long it works, or why it did not go (and, here, the link). */
export function ResetSent({ sent, to }: { sent: ResetAskedDto; to: string | null }) {
  const w = resetSentWords(sent, to);
  return (
    <div>
      <p className="text-lg">{w.line}</p>
      <p className="meta mt-2">{w.meta}</p>
      {sent.devLink ? (
        <p className="mt-4 text-sm text-ink-3">
          Development:{' '}
          <a className="link" href={sent.devLink}>
            open the link
          </a>
        </p>
      ) : null}
    </div>
  );
}
